import heydoctor from 'app/vendor/heydoctor';
import {
  Prescription,
  PrescriptionGetResponse,
  PrescriptionsGetResponse,
  PrescriptionPostResponse,
  Delivery,
} from 'app/types';
import { AxiosError } from 'axios';
import immer from 'immer';
import { Coupon } from 'app/types/Coupon';
import { Stores, Store } from 'app/lib/synaptik';

interface State {
  records: Prescription[];
  detail: null | Prescription;
  deliveries: Delivery[];
  coupons?: Record<string, Coupon>;

  // type depends on key: 'fetchingDetails' and key: 'fetchingList'  below
  fetchingDetails?: boolean;
  fetchingDetailsError?: AxiosError;
  fetchingList?: boolean;
  postingPickup?: boolean;
  creatingDelivery?: boolean;
  updatingDelivery?: boolean;
  fetchingCoupon?: boolean;
  fetchingDeliveries?: boolean;
  fetchingAvailability?: boolean;
}

interface PostPickupProps {
  prescriptionId: number;
  pharmacyId: number;
}

export class PrescriptionsStore extends Store<State, Stores> {
  state: State = {
    records: [],
    detail: null,
    deliveries: [],
    coupons: {},
  };

  postPickup = ({ prescriptionId, pharmacyId }: PostPickupProps) =>
    this.runAsync({
      key: 'postingPickup',
      work: async () => {
        const {
          data: { data },
        }: PrescriptionPostResponse = await heydoctor.client.http.post(`/prescriptions/${prescriptionId}/pickup`, {
          pharmacy_id: pharmacyId,
        });

        this.setState({
          detail: data,
        });

        return data;
      },
    });

  fetchDetail = (prescriptionId: number) =>
    this.runAsync({
      key: 'fetchingDetails',
      work: async () => {
        const {
          data: { data },
        }: PrescriptionGetResponse = await heydoctor.client.http.get(`/prescriptions/${prescriptionId}`);

        this.setState({
          detail: data,
        });

        return data;
      },
    });

  fetchList = () =>
    this.runAsync({
      key: 'fetchingList',
      work: async (): Promise<Prescription[]> => {
        const {
          data: { data },
        }: PrescriptionsGetResponse = await heydoctor.client.http.get('/prescriptions');

        this.setState({
          records: data,
        });

        return data;
      },
    });

  getDeliveryAvailability = async (prescriptionId: number) =>
    this.runAsync({
      key: 'fetchingAvailability',
      work: async () => {
        const {
          data: { data },
        } = await heydoctor.client.http.get(`/prescriptions/${prescriptionId}/delivery/availability`);

        return data;
      },
    });

  fetchDeliveries = async (prescriptionId: number) =>
    this.runAsync({
      key: 'fetchingDeliveries',
      work: async () => {
        const {
          data: { data },
        } = await heydoctor.client.http.get(`/prescriptions/${prescriptionId}/deliveries`);

        this.setState({
          deliveries: data,
        });

        return data;
      },
    });

  createDelivery = ({
    prescriptionId,
    paymentToken,
    shippingAddress,
  }: {
    paymentToken: string;
    shippingAddress: any;
    prescriptionId: number;
  }) =>
    this.runAsync({
      key: 'creatingDelivery',
      work: async () => {
        const {
          data: { data: delivery },
        } = await heydoctor.client.http.post(`/prescriptions/${prescriptionId}/delivery`, {
          payment_token: paymentToken,
          shipping_address: shippingAddress,
        });

        this.setState((state: State) => ({
          detail: delivery.prescription,
          deliveries: [...state.deliveries, delivery],
        }));

        return delivery;
      },
    });

  updateDelivery = ({
    prescriptionId,
    deliveryId,
    status,
    paymentToken,
    shippingAddress,
  }: {
    prescriptionId: number;
    deliveryId: number;
    status?: string;
    paymentToken?: string;
    shippingAddress?: Delivery['shippingAddress'];
  }) =>
    this.runAsync({
      key: 'updatingDelivery',
      work: async () => {
        const {
          data: { data: delivery },
        } = await heydoctor.client.http.patch(`/prescriptions/${prescriptionId}/delivery/${deliveryId}`, {
          status,
          payment_token: paymentToken,
          shipping_address: shippingAddress,
        });

        this.setState((state: any) => ({
          deliveries: [...state.deliveries.filter((d: any) => d.id !== deliveryId), delivery],
        }));

        return delivery;
      },
    });

  fetchCoupon = async (prescriptionId: number) =>
    this.runAsync({
      key: 'fetchingCoupon',
      work: async () => {
        const {
          data: { data },
        } = await heydoctor.client.http.get(`/prescriptions/${prescriptionId}/coupon`);

        this.setState(
          immer((draft) => {
            draft.coupons = { ...draft.coupons, [prescriptionId]: data };
          })
        );

        return data;
      },
    });

  sendCoupon = async (prescriptionId: number, channel: 'sms' | 'email') =>
    this.runAsync({
      work: () =>
        heydoctor.client.http.post(`/prescriptions/${prescriptionId}/send-coupon`, {
          channel,
        }),
    });
}
